import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import ArticleCard from '@/components/base/ArticleCard';
import ArticleBody from '@/pages/article/components/ArticleBody';
import ArticleSidebar from '@/pages/article/components/ArticleSidebar';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  getArticleById,
  getCategoryName,
  getRelatedArticles,
  formatCount,
  formatDate,
} from '@/lib/content';
import type { Article } from '@/types/content';

export default function ArticlePage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);

  usePageMeta(
    article ? `${article.title} - 猎奇吃瓜` : '内容不存在 - 猎奇吃瓜',
    article ? article.excerpt : '这条内容暂时找不到了，回首页继续吃瓜。',
  );

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-700">
            <i className="ri-ghost-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl text-foreground-950">这条瓜好像被人吃完了</h1>
          <p className="mt-3 text-sm text-foreground-600">内容可能已下线，回首页看看今天的新瓜。</p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 dark:text-foreground-950"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: 'zh-CN',
    author: { '@type': 'Person', name: article.author },
    publisher: { '@type': 'Organization', name: '猎奇吃瓜' },
    articleSection: getCategoryName(article.category),
    keywords: article.tags.join(','),
    image: article.cover,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ff41523.cn/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: getCategoryName(article.category),
        item: `https://ff41523.cn/category/${article.category}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <SiteHeader />
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: getCategoryName(article.category), to: `/category/${article.category}` },
          { label: '正文' },
        ]}
      />
      <main className="flex-1 px-4 py-10 md:px-8 md:py-12">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${article.category}`}
                  className="rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700 transition-colors hover:bg-primary-200"
                >
                  {getCategoryName(article.category)}
                </Link>
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  阅读约 {article.readTime}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">{article.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-4 border-y border-background-200 py-4 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line" aria-hidden="true"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {formatDate(article.date)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" aria-hidden="true"></i>
                  {formatCount(article.views)} 次围观
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line" aria-hidden="true"></i>
                  {formatCount(article.comments)} 条讨论
                </span>
              </div>
            </header>

            <div className="mt-6 h-64 w-full overflow-hidden rounded-lg bg-background-100 md:h-96">
              <img
                src={article.cover}
                alt={`${article.title} 相关插画`}
                title={`${article.title} - 猎奇吃瓜`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-10">
              <ArticleBody article={article} />
            </div>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
                <i className="ri-magic-line text-primary-600" aria-hidden="true"></i>
                相关推荐
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item: Article) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </section>
          </div>

          <ArticleSidebar />
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}